export const MEETING_DURATION_MINUTES = 15

// One colour per status everywhere a meeting appears (chips, calendar events,
// stat cards): blue upcoming, green completed, amber rescheduled, red cancelled.
// The chip colours are explicit because the admin theme restyles MUI chips.
export const MEETING_STATUS_META = {
  upcoming: {
    label: "Upcoming",
    calendarColor: "#2563eb",
    accent: "info",
    chip: { background: "#DBEAFE", text: "#1D4ED8", border: "#93C5FD" },
  },
  completed: {
    label: "Completed",
    calendarColor: "#16a34a",
    accent: "success",
    chip: { background: "#DCFCE7", text: "#15803D", border: "#86EFAC" },
  },
  rescheduled: {
    label: "Rescheduled",
    calendarColor: "#d97706",
    accent: "warning",
    chip: { background: "#FEF3C7", text: "#B45309", border: "#FCD34D" },
  },
  cancelled: {
    label: "Cancelled",
    calendarColor: "#dc2626",
    accent: "danger",
    chip: { background: "#FEE2E2", text: "#B91C1C", border: "#FCA5A5" },
  },
}

const NEUTRAL_STATUS_META = {
  calendarColor: "#6b7280",
  accent: "neutral",
  chip: { background: "#F3F4F6", text: "#374151", border: "#D1D5DB" },
}

export const MEETING_STATUS_FILTER_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "upcoming", label: "Upcoming" },
  { value: "completed", label: "Completed" },
  { value: "rescheduled", label: "Rescheduled" },
  { value: "cancelled", label: "Cancelled" },
]

export const PROVIDER_LABELS = {
  gmail: "Google",
  ms: "Outlook",
  acuity: "Acuity",
}

export function statusMeta(status) {
  return (
    MEETING_STATUS_META[status] || {
      label: status || "—",
      ...NEUTRAL_STATUS_META,
    }
  )
}

export function providerLabel(provider) {
  return PROVIDER_LABELS[provider] || provider || "—"
}

export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  } catch (error) {
    return "UTC"
  }
}

const FALLBACK_TIME_ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Vancouver",
  "Europe/London",
  "Asia/Calcutta",
  "UTC",
]

export function getTimeZoneOptions() {
  let zones = FALLBACK_TIME_ZONES
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      zones = Intl.supportedValuesOf("timeZone")
    }
  } catch (error) {
    zones = FALLBACK_TIME_ZONES
  }
  const current = browserTimeZone()
  return zones.includes(current) ? zones : [current, ...zones]
}

// Time zones an agent is most likely to be in; shown in the booking form.
export const AGENT_TIME_ZONES = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HST)" },
  { value: "America/Toronto", label: "Toronto" },
  { value: "America/Vancouver", label: "Vancouver" },
  { value: "Other", label: "Other" },
]

export function formatMeetingDateTime(value, timeZone) {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  try {
    return date.toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
      ...(timeZone ? { timeZone } : {}),
    })
  } catch (error) {
    return date.toLocaleString("en-US")
  }
}

export function formatSlotTime(slot, timeZone) {
  const date = new Date(slot.start)
  try {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone,
    })
  } catch (error) {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })
  }
}

// "2026-11-02T10:30" from a <input type="datetime-local"> -> ISO with offset.
export function localInputToIso(value) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

export function getApiErrorMessage(error, fallback = "Something went wrong.") {
  const data = error?.response?.data
  if (!data) return fallback
  if (typeof data === "string") return data
  if (typeof data.detail === "string") return data.detail
  const messages = Object.entries(data).flatMap(([field, value]) => {
    const text = Array.isArray(value) ? value.join(" ") : String(value)
    return field === "detail"
      ? [text]
      : [`${field.replace(/_/g, " ")}: ${text}`]
  })
  return messages.length ? messages.join(" ") : fallback
}

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (error) {
    return false
  }
}

// The booking details people share: the form answers saved with the meeting.
export function meetingDetailsText(meeting) {
  const notes = (meeting?.description || "").trim()
  return notes ? `Booking form / notes\n${notes}` : ""
}

export const REMINDER_UNITS = [
  { value: "minutes", label: "Minutes", size: 1 },
  { value: "hours", label: "Hours", size: 60 },
  { value: "days", label: "Days", size: 1440 },
]
export const MAX_REMINDER_MINUTES = 7 * 24 * 60

// The amount and unit an admin typed, in minutes (NaN when it is not a number).
export function reminderMinutes(amount, unit) {
  const size = REMINDER_UNITS.find((option) => option.value === unit)?.size ?? 1
  const value = Number(amount)
  return Number.isFinite(value) && Number.isInteger(value) ? value * size : NaN
}

export function validateReminderMinutes(minutes) {
  if (!Number.isInteger(minutes) || minutes < 1) {
    return "Enter a whole number of 1 or more."
  }
  if (minutes > MAX_REMINDER_MINUTES) return "The most is 7 days."
  return ""
}

// 15 -> "15 minutes", 60 -> "1 hour", 1440 -> "1 day".
export function describeReminderLead(minutes) {
  for (const [size, unit] of [
    [1440, "day"],
    [60, "hour"],
    [1, "minute"],
  ]) {
    if (minutes % size === 0) {
      const count = minutes / size
      return `${count} ${unit}${count === 1 ? "" : "s"}`
    }
  }
  return `${minutes} minutes`
}

export function isReauthorizeError(error) {
  return error?.response?.data?.reauthorize === true
}

export const BOOKING_STEPS = [
  "Select user",
  "Date & time",
  "Candidate details",
  "Confirmed",
]

export function emptyBookingForm() {
  return {
    host: null,
    date: "",
    timeZone: browserTimeZone(),
    slot: null,
    candidate: null,
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    liaison: "",
    companyName: "",
    city: "",
    state: "",
    agentTimeZone: "",
    yearsInBusiness: "",
    salesVolume: "",
    homesSold: "",
    notes: "",
    answers: {},
  }
}

export function candidateToFormFields(candidate) {
  const [firstName = "", ...rest] = (candidate?.candidate_name || "")
    .trim()
    .split(/\s+/)
  const text = (value) =>
    value === null || value === undefined ? "" : String(value)
  return {
    candidate,
    firstName,
    lastName: rest.join(" "),
    phone: text(candidate?.phone),
    email: text(candidate?.email),
    companyName: text(candidate?.company_name),
    city: text(candidate?.city),
    state: text(candidate?.state),
    yearsInBusiness: text(candidate?.years_in_business),
    salesVolume: text(candidate?.sales_volume_last_year),
    homesSold: text(candidate?.homes_sold_last_year),
  }
}

// The booking form's questions. Staff manage them (Meetings > Configuration); the
// server sends the active ones. The built-in ones keep their own form fields,
// because OneBase copies them onto the candidate; any other question is answered
// in ``form.answers`` by its key.
export const BUILTIN_QUESTION_FIELDS = {
  appointment_liaison: "liaison",
  company_name: "companyName",
  city: "city",
  state: "state",
  agent_time_zone: "agentTimeZone",
  years_in_business: "yearsInBusiness",
  sales_volume: "salesVolume",
  homes_sold: "homesSold",
  notes: "notes",
}
const BUILTIN_NUMBER_LIMITS = {
  years_in_business: 100,
  sales_volume: 2000000000,
  homes_sold: 30000,
}
const BUILTIN_REQUIRED_MESSAGES = {
  appointment_liaison: "Select an appointment liaison.",
  company_name: "Current brokerage is required.",
  city: "City is required.",
  state: "State is required.",
  agent_time_zone: "Select the agent's time zone.",
  notes: "Additional notes are required.",
}

// The form as it is before staff change anything. Used until the server's list
// arrives, and by anything that does not load it.
export const DEFAULT_BOOKING_QUESTIONS = [
  ["appointment_liaison", "Appointment Liaison", "dropdown", true],
  ["company_name", "Current Brokerage", "text", true],
  ["city", "City", "text", true],
  ["state", "State", "text", true],
  ["agent_time_zone", "Agent Time Zone", "dropdown", true],
  ["years_in_business", "Years in the business", "number", false],
  ["sales_volume", "Production Volume (last 12 months)", "number", false],
  ["homes_sold", "Number of Sales (last 12 months)", "number", false],
  ["notes", "Additional Notes", "long_text", true],
].map(([key, label, field_type, required]) => ({
  key,
  label,
  help_text: "",
  field_type,
  required,
  is_builtin: true,
  options: [],
}))

// Where errors for a question are kept: the form field for a built-in one.
export function questionErrorKey(question) {
  return question.is_builtin
    ? BUILTIN_QUESTION_FIELDS[question.key]
    : question.key
}

export function getAnswer(form, question) {
  const value = question.is_builtin
    ? form[BUILTIN_QUESTION_FIELDS[question.key]]
    : form.answers?.[question.key]
  return value ?? ""
}

// The form with one answer changed.
export function withAnswer(form, question, value) {
  if (question.is_builtin) {
    return { ...form, [BUILTIN_QUESTION_FIELDS[question.key]]: value }
  }
  return { ...form, answers: { ...form.answers, [question.key]: value } }
}

function validateAnswer(question, value) {
  const text = String(value ?? "").trim()
  if (!text) {
    if (!question.required) return ""
    return (
      BUILTIN_REQUIRED_MESSAGES[question.is_builtin ? question.key : ""] ||
      `${question.label} is required.`
    )
  }
  if (question.field_type === "number") {
    const number = toNumberOrNull(text)
    const limit = BUILTIN_NUMBER_LIMITS[question.key]
    if (
      Number.isNaN(number) ||
      (limit !== undefined && (number < 0 || number > limit))
    ) {
      return "Enter a valid number."
    }
  }
  return ""
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function toNumberOrNull(value) {
  const cleaned = String(value ?? "").replace(/[$,\s]/g, "")
  if (cleaned === "") return null
  const number = Number(cleaned)
  return Number.isFinite(number) ? number : NaN
}

export function validateBookingStep(
  step,
  form,
  questions = DEFAULT_BOOKING_QUESTIONS
) {
  const errors = {}
  if (step === 0) {
    if (!form.host) errors.host = "Select a user."
    else if (!form.host.connected)
      errors.host = "This user has not connected Google or Outlook."
  }
  if (step === 1) {
    if (!form.date) errors.date = "Pick a date."
    if (!form.slot) errors.slot = "Pick a time."
  }
  if (step === 2) {
    const required = {
      firstName: "First name is required.",
      lastName: "Last name is required.",
      phone: "Phone is required.",
      email: "Email is required.",
    }
    Object.entries(required).forEach(([field, message]) => {
      if (!String(form[field] || "").trim()) errors[field] = message
    })
    if (!errors.email && !EMAIL_PATTERN.test(form.email.trim()))
      errors.email = "Enter a valid email address."
    questions.forEach((question) => {
      const message = validateAnswer(question, getAnswer(form, question))
      if (message) errors[questionErrorKey(question)] = message
    })
  }
  return errors
}

export function buildBookingPayload(
  form,
  questions = DEFAULT_BOOKING_QUESTIONS
) {
  const payload = {
    user_id: form.host?.id,
    candidate_id: form.candidate?.id ?? null,
    start_datetime: form.slot?.start,
    duration_minutes: MEETING_DURATION_MINUTES,
    timezone: form.timeZone,
    first_name: form.firstName.trim(),
    last_name: form.lastName.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
  }
  const answers = {}
  questions.forEach((question) => {
    const value = getAnswer(form, question)
    if (question.is_builtin) {
      payload[question.key] =
        BUILTIN_NUMBER_LIMITS[question.key] !== undefined
          ? toNumberOrNull(value)
          : String(value).trim()
    } else if (String(value).trim()) {
      answers[question.key] = String(value).trim()
    }
  })
  return { ...payload, answers }
}

export function meetingCandidateNames(meeting) {
  const names = (meeting?.candidates || [])
    .map((candidate) => candidate.candidate_name)
    .filter(Boolean)
  return names.length ? names.join(", ") : "—"
}

const HISTORY_ACTIONS = {
  created: { verb: "Booked", color: "#2563eb" },
  rescheduled: { verb: "Rescheduled", color: "#d97706" },
  cancelled: { verb: "Cancelled", color: "#dc2626" },
}

// One step of a meeting's history as display text: who did what, when, the old
// and new time, and the reason.
export function describeHistoryEntry(entry) {
  const meta = HISTORY_ACTIONS[entry.action] || {
    verb: entry.action,
    color: "#6b7280",
  }
  // Changes made in Google or Outlook itself have no OneBase user to name.
  let title = `${meta.verb} (user not recorded)`
  if (entry.calendar_source) {
    title = `${meta.verb} in the ${entry.calendar_source} calendar`
  } else if (entry.performed_by_name) {
    title = `${meta.verb} by ${entry.performed_by_name}`
  }
  let detail = null
  if (entry.action === "created" && entry.new_start) {
    detail = `For ${formatMeetingDateTime(entry.new_start)}`
  } else if (entry.action === "rescheduled" && entry.new_start) {
    detail = entry.previous_start
      ? `${formatMeetingDateTime(
          entry.previous_start
        )} → ${formatMeetingDateTime(entry.new_start)}`
      : `Moved to ${formatMeetingDateTime(entry.new_start)}`
  } else if (entry.action === "cancelled" && entry.previous_start) {
    detail = `Was ${formatMeetingDateTime(entry.previous_start)}`
  }
  return {
    title,
    at: formatMeetingDateTime(entry.at),
    detail,
    // The title already says where the change was made.
    reason: entry.calendar_source ? null : entry.reason || null,
    color: meta.color,
  }
}

const BOOKER_REQUIRED = {
  bookerName: "Enter your name.",
  bookerEmail: "Enter your email.",
}

// The public booking page also asks who is booking, since they have no login.
export function validatePublicBooker(form) {
  const errors = {}
  Object.entries(BOOKER_REQUIRED).forEach(([field, message]) => {
    if (!String(form[field] || "").trim()) errors[field] = message
  })
  if (!errors.bookerEmail && !EMAIL_PATTERN.test(form.bookerEmail.trim()))
    errors.bookerEmail = "Enter a valid email address."
  return errors
}

// The public booking payload: no user, candidate or duration. The server takes
// the user from the link and fixes the length.
export function buildPublicBookingPayload(
  form,
  questions = DEFAULT_BOOKING_QUESTIONS
) {
  const {
    // eslint-disable-next-line no-unused-vars
    user_id,
    // eslint-disable-next-line no-unused-vars
    candidate_id,
    // eslint-disable-next-line no-unused-vars
    duration_minutes,
    ...fields
  } = buildBookingPayload(form, questions)
  return {
    ...fields,
    booker_name: form.bookerName.trim(),
    booker_email: form.bookerEmail.trim(),
    website: form.website || "",
  }
}
